// ============= Imports =============

import { AuthContextProps } from '@/contexts/AuthContext';
import { tokenManager } from '@tw/tw-component-library';

interface deleteDocumentType {
  blobURL: string;
  getAccessToken: AuthContextProps['getAccessToken'];
  spoofedUser: string | null;
}

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_URL;
const API_ENDPOINT = '/api/Document';
const AXIS_SCOPE = 'api://f23d5e95-70a8-4aca-b373-e23d8c9f6454/access_as_user';

/**
 * Creates headers for API request
 * @param token Authentication token
 * @param isFileUpload Whether this is a file upload request
 * @returns Headers object
 */
const createHeaders = (
  token: string,
  spoofedUser: string | null
): Record<string, string> => {
  const baseHeaders: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    ...(spoofedUser && {
      'x-Spoof-User': spoofedUser,
    }),
  } as Record<string, string>;

  return baseHeaders;
};

// ============= Main Function =============
/**
 * Deletes a document using its blob URL
 * @param blobURL The blob URL of the document to delete
 * @param getAccessToken Function to acquire access token
 * @param spoofedUser Optional user to spoof for the request
 * @returns Promise with API response containing success status and data or error
 */
export const deleteDocument = async ({
  blobURL,
  getAccessToken,
  spoofedUser,
}: deleteDocumentType) => {
  try {
    console.log('🔄 Deleting document...');

    // Token acquisition with required scopes
    const token = await getAccessToken([AXIS_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    // URL construction with blobURL as query parameter
    const url = `${BASE_URL}${API_ENDPOINT}?blobURL=${encodeURIComponent(
      blobURL
    )}`;
    console.log('🌐 Making request to:', url);

    // Prepare request body for DELETE request
    const requestBody = JSON.stringify({ blobURL });

    // Build headers for JSON request
    const headers = createHeaders(token, spoofedUser || null);
    headers['Content-Type'] = 'application/json';

    // API request
    const response = await fetch(url, {
      method: 'DELETE',
      body: requestBody,
      headers,
    });

    // Response handling
    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      const fieldErrors: Record<string, string> = {};

      try {
        const contentType = response.headers.get('content-type') || '';
        let errorData: unknown = null;
        if (contentType.includes('application/json')) {
          errorData = await response.json();
        } else {
          errorData = await response.text();
        }

        if (typeof errorData === 'string') {
          const trimmed = errorData.trim();
          if (trimmed) {
            errorMessage = `${errorMessage}: ${trimmed}`;
          }
        } else if (errorData && typeof errorData === 'object') {
          const asRecord = errorData as Record<string, unknown>;
          // Some APIs use 'message' or 'title' for primary error text
          const primary = (asRecord.message || asRecord.title) as
            | string
            | undefined;
          if (typeof primary === 'string' && primary) {
            errorMessage = `${errorMessage}: ${primary}`;
          }
          if (asRecord.errors && typeof asRecord.errors === 'object') {
            const errorsObj = asRecord.errors as Record<string, unknown>;
            Object.entries(errorsObj).forEach(([field, messages]) => {
              if (Array.isArray(messages)) {
                fieldErrors[field] = messages.join(' ');
              } else if (typeof messages === 'string') {
                fieldErrors[field] = messages;
              }
            });
          }
        }
      } catch {
        errorMessage = `${errorMessage}: ${
          response.statusText || 'Unknown error'
        }`;
      }

      console.log('❌ Document deletion failed:', errorMessage, fieldErrors);
      return {
        success: false,
        error: errorMessage,
        errors: fieldErrors,
        status: response.status,
      } as const;
    }

    let data: unknown = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }
    console.log('✅ Document deleted successfully');

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('🔴 Error deleting document:', error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unknown error occurred',
    };
  }
};
